import { api } from './api'

/**
 * Web TTS playback aligned with Chrome's play() promise rules:
 * https://developer.chrome.com/blog/play-request-was-interrupted
 *
 * - Always handle the Promise from HTMLMediaElement.play()
 * - Never pause()/load()/clear src until that Promise settles
 * - On user gesture call load() synchronously (do not mark the click
 *   handler async) so a later play() after fetch is still allowed
 */

export type SpeakOptions = {
  /** Fires when playback starts (or fails / is cancelled while waiting). */
  onReveal?: () => void
  /** Fires when TTS fetch or playback fails for this utterance. */
  onError?: (message: string) => void
}

type SpeakJob = {
  text: string
  key?: string
  onReveal?: () => void
  onError?: (message: string) => void
  audioPromise: Promise<string>
  revealed: boolean
}

/** One reused element so gesture-time load() authorizes later play(). */
let sharedAudio: HTMLAudioElement | null = null
/**
 * Promise for the current play() call. Chrome requires this to settle
 * before pause()/src changes: https://developer.chrome.com/blog/play-request-was-interrupted
 */
let currentPlayPromise: Promise<void> | null = null
/** Resolves when the current utterance finishes or is stopped. */
let currentPlaybackDone: (() => void) | null = null

let playGeneration = 0
let playingKey: string | null = null
const playingListeners = new Set<(key: string | null) => void>()
const speakQueue: SpeakJob[] = []
let queueRunning = false
const spokenOrQueued = new Set<string>()
let audioUnlocked = false

const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA='

export function getPlayingKey(): string | null {
  return playingKey
}

export function isAudioUnlocked(): boolean {
  return audioUnlocked
}

/** Test-only: clear module singletons between vitest cases. */
export function resetVoiceForTests() {
  sharedAudio = null
  currentPlayPromise = null
  currentPlaybackDone = null
  playGeneration = 0
  playingKey = null
  speakQueue.length = 0
  queueRunning = false
  spokenOrQueued.clear()
  audioUnlocked = false
  voiceParts.clear()
  voiceDone.clear()
  for (const url of voiceObjectUrls.values()) URL.revokeObjectURL(url)
  voiceObjectUrls.clear()
  voiceWaiters.clear()
  mseSessions.clear()
  enqueuedLive.clear()
}

export function subscribePlayingKey(listener: (key: string | null) => void): () => void {
  playingListeners.add(listener)
  return () => {
    playingListeners.delete(listener)
  }
}

function getSharedAudio(): HTMLAudioElement {
  if (!sharedAudio) {
    sharedAudio = new Audio()
    sharedAudio.preload = 'auto'
    sharedAudio.setAttribute('playsinline', 'true')
    // Keep a live element; never remove it from use between utterances.
  }
  return sharedAudio
}

function setPlayingKey(key: string | null) {
  playingKey = key
  for (const listener of playingListeners) listener(key)
}

/**
 * Call SYNCHRONOUSLY from a click/tap handler (not after await).
 * Chrome: `load()` during the user gesture preserves the ability to play()
 * after an async fetch. Do not mark the click handler `async`.
 */
export function unlockAudioPlayback(): void {
  if (typeof window === 'undefined') return

  const audio = getSharedAudio()

  // Gesture-time load() — see Chrome "Fetch & Play" example.
  try {
    if (!audio.src) audio.src = SILENT_WAV
    audio.load()
  } catch {
    // ignore
  }

  // Sticky unlock: play silence and only pause AFTER play() fulfills.
  const playAttempt = audio.play()
  currentPlayPromise = playAttempt
    .then(() => {
      audioUnlocked = true
      try {
        audio.pause()
        audio.currentTime = 0
      } catch {
        // ignore
      }
    })
    .catch(() => {
      // Autoplay still blocked — bubble play may still work after load().
    })
    .finally(() => {
      if (currentPlayPromise === playAttempt) currentPlayPromise = null
    })
}

function revealJob(job: SpeakJob) {
  if (job.revealed) return
  job.revealed = true
  try {
    job.onReveal?.()
  } catch {
    // ignore
  }
}

function failJob(job: SpeakJob, err: unknown) {
  revealJob(job)
  if (isPlaybackInterrupted(err)) return
  const message =
    err instanceof Error ? err.message : typeof err === 'string' ? err : 'Could not play voice'
  if (!message) return
  try {
    job.onError?.(message)
  } catch {
    // ignore
  }
}

function isPlaybackInterrupted(err: unknown): boolean {
  if (!err) return false
  const name = typeof err === 'object' && 'name' in err ? String((err as Error).name) : ''
  const message = err instanceof Error ? err.message : String(err)
  return (
    name === 'AbortError' ||
    /interrupted|removed from the document|The play\(\) request was interrupted/i.test(message)
  )
}

/** Wait for any in-flight play() before mutating the element (Chrome requirement). */
async function settleCurrentPlay(): Promise<void> {
  const pending = currentPlayPromise
  if (!pending) return
  try {
    await pending
  } catch {
    // ignore rejection — we only need it settled
  }
}

async function stopCurrentElement(): Promise<void> {
  await settleCurrentPlay()
  const audio = sharedAudio
  if (!audio) return
  const onEnded = audio.onended
  audio.onended = null
  audio.onerror = null
  try {
    // play() has settled (or never started) — safe to pause per Chrome docs.
    audio.pause()
    audio.currentTime = 0
  } catch {
    // ignore
  }
  // Unblock playOne waiters that are listening for 'ended'.
  try {
    onEnded?.call(audio, new Event('ended'))
  } catch {
    // ignore
  }
  currentPlaybackDone?.()
  currentPlaybackDone = null
}

async function fetchTtsObjectUrl(text: string): Promise<string> {
  // HTTP fallback for tests / environments without streamed agent voice.
  const response = await api.fetch('/voice/tts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  })
  if (!response.ok) {
    const errText = await response.text().catch(() => '')
    throw new Error(`TTS failed (${response.status}): ${errText.slice(0, 120)}`)
  }
  const buffer = await response.arrayBuffer()
  if (buffer.byteLength === 0) throw new Error('TTS returned empty audio')
  const blob = new Blob([buffer], { type: 'audio/mpeg' })
  return URL.createObjectURL(blob)
}

/** Base64 mp3 fragments keyed by messageId (agent WS voice chunks). */
const voiceParts = new Map<string, Uint8Array[]>()
const voiceDone = new Set<string>()
const voiceObjectUrls = new Map<string, string>()
const voiceWaiters = new Map<
  string,
  { resolve: (url: string) => void; reject: (err: Error) => void }[]
>()
/** Progressive MSE sessions (Chrome); Safari falls back to full-blob. */
const mseSessions = new Map<
  string,
  {
    mediaSource: MediaSource
    sourceBuffer: SourceBuffer | null
    queue: Uint8Array[]
    updating: boolean
    ended: boolean
    url: string
  }
>()
const enqueuedLive = new Set<string>()

const mseSupported =
  typeof MediaSource !== 'undefined' &&
  typeof MediaSource.isTypeSupported === 'function' &&
  MediaSource.isTypeSupported('audio/mpeg')

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
  let total = 0
  for (const p of parts) total += p.byteLength
  const out = new Uint8Array(total)
  let offset = 0
  for (const p of parts) {
    out.set(p, offset)
    offset += p.byteLength
  }
  return out
}

function settleVoiceWaiters(messageId: string, url: string | null, err?: Error) {
  const waiters = voiceWaiters.get(messageId)
  if (!waiters?.length) return
  voiceWaiters.delete(messageId)
  for (const w of waiters) {
    if (url) w.resolve(url)
    else w.reject(err ?? new Error('No audio for message'))
  }
}

/** Fail an in-flight voice stream so waiters and held bubbles release. */
export function failVoiceStream(messageId: string, reason = 'Voice playback failed'): void {
  if (!messageId) return
  voiceParts.delete(messageId)
  const session = mseSessions.get(messageId)
  if (session) {
    try {
      if (session.mediaSource.readyState === 'open') session.mediaSource.endOfStream()
    } catch {
      // ignore
    }
    mseSessions.delete(messageId)
  }
  settleVoiceWaiters(messageId, null, new Error(reason))
}

function pumpMse(messageId: string) {
  const session = mseSessions.get(messageId)
  if (!session?.sourceBuffer || session.updating) return
  if (session.queue.length === 0) {
    if (session.ended && session.mediaSource.readyState === 'open') {
      try {
        session.mediaSource.endOfStream()
      } catch {
        // ignore
      }
    }
    return
  }
  const next = session.queue.shift()!
  session.updating = true
  try {
    const ab = new ArrayBuffer(next.byteLength)
    new Uint8Array(ab).set(next)
    session.sourceBuffer.appendBuffer(ab)
  } catch (err) {
    session.updating = false
    console.warn('[voice] MSE append failed', err)
  }
}

function ensureMseSession(messageId: string): string {
  const existing = mseSessions.get(messageId)
  if (existing) return existing.url

  const mediaSource = new MediaSource()
  const url = URL.createObjectURL(mediaSource)
  const session = {
    mediaSource,
    sourceBuffer: null as SourceBuffer | null,
    queue: [] as Uint8Array[],
    updating: false,
    ended: false,
    url,
  }
  mseSessions.set(messageId, session)
  voiceObjectUrls.set(messageId, url)

  mediaSource.addEventListener('sourceopen', () => {
    try {
      const sb = mediaSource.addSourceBuffer('audio/mpeg')
      session.sourceBuffer = sb
      sb.addEventListener('updateend', () => {
        session.updating = false
        pumpMse(messageId)
      })
      pumpMse(messageId)
    } catch (err) {
      console.warn('[voice] MSE sourceBuffer failed', err)
      settleVoiceWaiters(messageId, null, err instanceof Error ? err : new Error('MSE failed'))
    }
  })

  return url
}

/**
 * Ingest a streamed voice chunk from the agent WebSocket.
 * Starts progressive playback ASAP (MSE when supported); finalizes on done.
 */
export function pushVoiceChunk(messageId: string, b64: string, done: boolean): void {
  if (!messageId) return

  if (b64) {
    const bytes = b64ToBytes(b64)
    const parts = voiceParts.get(messageId) ?? []
    parts.push(bytes)
    voiceParts.set(messageId, parts)

    if (mseSupported) {
      const url = ensureMseSession(messageId)
      const session = mseSessions.get(messageId)!
      session.queue.push(bytes)
      pumpMse(messageId)
      // Resolve waiters as soon as progressive URL exists so play can start.
      settleVoiceWaiters(messageId, url)
    }
  }

  if (!done) return

  voiceDone.add(messageId)

  if (mseSupported) {
    const session = mseSessions.get(messageId)
    if (session) {
      session.ended = true
      pumpMse(messageId)
      settleVoiceWaiters(messageId, session.url)
    } else if ((voiceParts.get(messageId) ?? []).length === 0) {
      settleVoiceWaiters(messageId, null, new Error('Empty voice stream'))
    }
    voiceParts.delete(messageId)
    return
  }

  // Safari / no MSE: assemble full blob then play.
  const parts = voiceParts.get(messageId) ?? []
  voiceParts.delete(messageId)
  if (parts.length === 0) {
    settleVoiceWaiters(messageId, null, new Error('Empty voice stream'))
    return
  }
  const bytes = concatBytes(parts)
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  const blob = new Blob([copy], { type: 'audio/mpeg' })
  const prev = voiceObjectUrls.get(messageId)
  if (prev) URL.revokeObjectURL(prev)
  const url = URL.createObjectURL(blob)
  voiceObjectUrls.set(messageId, url)
  settleVoiceWaiters(messageId, url)
}

const VOICE_WAIT_MS = 45_000

function waitForVoiceUrl(messageId: string, timeoutMs = VOICE_WAIT_MS): Promise<string> {
  const existing = voiceObjectUrls.get(messageId)
  if (existing) return Promise.resolve(existing)
  return new Promise((resolve, reject) => {
    const list = voiceWaiters.get(messageId) ?? []
    const entry = { resolve, reject }
    list.push(entry)
    voiceWaiters.set(messageId, list)
    setTimeout(() => {
      const current = voiceWaiters.get(messageId)
      if (!current?.includes(entry)) return
      failVoiceStream(messageId, 'Voice timed out')
    }, timeoutMs)
  })
}

/**
 * Queue playback of voice already streamed (or still streaming) for messageId.
 * Does not interrupt other messages — joins the speak queue.
 * Safe to call on first chunk (progressive) or at end.
 */
export function enqueueMessageVoice(messageId: string, options?: SpeakOptions): void {
  if (!messageId) {
    options?.onReveal?.()
    return
  }
  if (spokenOrQueued.has(messageId) || playingKey === messageId) {
    options?.onReveal?.()
    return
  }
  spokenOrQueued.add(messageId)
  enqueuedLive.add(messageId)
  speakQueue.push({
    text: '',
    key: messageId,
    onReveal: options?.onReveal,
    onError: options?.onError,
    audioPromise: waitForVoiceUrl(messageId),
    revealed: false,
  })
  void runSpeakQueue()
}

/**
 * Play audio for a chat message.
 * Register waiter → requestPlay() → stop other audio (not this waiter) → play.
 * If agent WS voice fails and textFallback is set, uses POST /voice/tts (same as live HTTP path).
 */
export async function playMessageVoice(
  messageId: string,
  requestPlay?: () => void,
  textFallback?: string,
): Promise<void> {
  if (!messageId) return
  // Toggle off when this bubble is already the active utterance.
  if (playingKey === messageId) {
    await stopSpeaking()
    return
  }
  // Must run synchronously on the click stack (before any await).
  unlockAudioPlayback()

  const cached = voiceObjectUrls.get(messageId)
  if (cached) {
    await stopSpeaking()
    spokenOrQueued.add(messageId)
    speakQueue.push({
      text: '',
      key: messageId,
      revealed: true,
      audioPromise: Promise.resolve(cached),
    })
    await runSpeakQueue()
    return
  }

  const hasText = Boolean(textFallback?.trim())
  const waitMs = hasText ? 12_000 : VOICE_WAIT_MS
  const audioPromise = waitForVoiceUrl(messageId, waitMs).catch(async (err) => {
    const text = textFallback?.trim()
    if (!text) throw err
    return fetchTtsObjectUrl(text)
  })
  requestPlay?.()

  playGeneration += 1
  for (const job of speakQueue) {
    if (job.key !== messageId) revealJob(job)
  }
  speakQueue.length = 0
  queueRunning = false
  spokenOrQueued.clear()
  setPlayingKey(null)
  await stopCurrentElement()

  spokenOrQueued.add(messageId)
  let lastError: Error | null = null
  speakQueue.push({
    text: '',
    key: messageId,
    revealed: true,
    audioPromise,
    onError: (message) => {
      lastError = new Error(message)
    },
  })
  await runSpeakQueue()
  if (lastError) throw lastError
}

export async function stopSpeaking() {
  playGeneration += 1
  for (const job of speakQueue) revealJob(job)
  speakQueue.length = 0
  queueRunning = false
  spokenOrQueued.clear()
  setPlayingKey(null)
  await stopCurrentElement()
}

export function enqueueSpeak(text: string, key?: string, options?: SpeakOptions): void {
  const trimmed = text.trim()
  if (!trimmed) {
    options?.onReveal?.()
    return
  }
  if (key) {
    if (spokenOrQueued.has(key) || playingKey === key) {
      options?.onReveal?.()
      return
    }
    spokenOrQueued.add(key)
  }
  speakQueue.push({
    text: trimmed,
    key,
    onReveal: options?.onReveal,
    onError: options?.onError,
    audioPromise: fetchTtsObjectUrl(trimmed),
    revealed: false,
  })
  void runSpeakQueue()
}

export async function speakText(text: string, key?: string): Promise<void> {
  const trimmed = text.trim()
  if (!trimmed) return
  if (key && playingKey === key) {
    await stopSpeaking()
    return
  }

  let lastError: Error | null = null
  // Caller must have invoked unlockAudioPlayback() synchronously on click.
  await stopSpeaking()
  if (key) spokenOrQueued.add(key)
  speakQueue.push({
    text: trimmed,
    key,
    audioPromise: fetchTtsObjectUrl(trimmed),
    revealed: true,
    onError: (message) => {
      lastError = new Error(message)
    },
  })
  await runSpeakQueue()
  if (lastError) throw lastError
}

async function runSpeakQueue() {
  if (queueRunning) return
  queueRunning = true
  try {
    while (speakQueue.length > 0) {
      const genAtStart = playGeneration
      const job = speakQueue.shift()!
      try {
        await playOne(job, genAtStart)
      } catch (err) {
        if (!isPlaybackInterrupted(err) && genAtStart === playGeneration) {
          failJob(job, err)
        } else {
          revealJob(job)
        }
      }
      if (genAtStart !== playGeneration) {
        revealJob(job)
        break
      }
    }
  } finally {
    queueRunning = false
    if (speakQueue.length > 0) void runSpeakQueue()
  }
}

function playFailureMessage(err: unknown): string {
  if (isPlaybackInterrupted(err)) return ''
  const name = err && typeof err === 'object' && 'name' in err ? String((err as Error).name) : ''
  const message = err instanceof Error ? err.message : String(err ?? '')
  if (
    name === 'NotAllowedError' ||
    /notallowed|user didn't interact|user gesture|play\(\) failed because/i.test(message)
  ) {
    return 'Tap play on a message to hear Rheya (browser blocked autoplay).'
  }
  return message || 'Could not play voice'
}

async function playOne(job: SpeakJob, gen: number): Promise<void> {
  if (gen !== playGeneration) {
    revealJob(job)
    return
  }

  let url: string
  try {
    url = await job.audioPromise
  } catch (err) {
    if (gen === playGeneration) setPlayingKey(null)
    throw err
  }

  if (gen !== playGeneration) {
    URL.revokeObjectURL(url)
    revealJob(job)
    return
  }

  // Wait for any prior play() before changing src / pausing (Chrome blog).
  await settleCurrentPlay()
  if (gen !== playGeneration) {
    URL.revokeObjectURL(url)
    revealJob(job)
    return
  }

  const audio = getSharedAudio()
  // Previous play has settled — safe to pause and swap source.
  try {
    audio.pause()
    audio.currentTime = 0
  } catch {
    // ignore
  }

  audio.src = url

  if (job.key) setPlayingKey(job.key)
  else setPlayingKey(null)
  revealJob(job)

  try {
    await new Promise<void>((resolve, reject) => {
      let settled = false
      const finish = (fn: () => void) => {
        if (settled) return
        settled = true
        audio.onended = null
        audio.onerror = null
        currentPlaybackDone = null
        fn()
      }

      currentPlaybackDone = () => finish(resolve)
      audio.onended = () => finish(resolve)
      audio.onerror = () => finish(() => reject(new Error('Audio playback failed')))

      // Always handle the play() promise (Chrome requirement).
      const playAttempt = audio.play()
      currentPlayPromise = playAttempt
        .then(() => {
          audioUnlocked = true
          // Do not pause here — wait for onended (or stopSpeaking).
        })
        .catch((err) => {
          if (isPlaybackInterrupted(err) || gen !== playGeneration) {
            finish(resolve)
            return
          }
          const msg = playFailureMessage(err)
          finish(() => (msg ? reject(new Error(msg)) : resolve()))
        })
        .finally(() => {
          if (currentPlayPromise === playAttempt) currentPlayPromise = null
        })
    })
  } finally {
    if (gen === playGeneration) setPlayingKey(null)
    try {
      URL.revokeObjectURL(url)
    } catch {
      // ignore
    }
  }
}

/** Transcribe a Blob (e.g. from MediaRecorder) via backend STT. */
export async function transcribeBlob(blob: Blob, filename = 'recording.webm'): Promise<string> {
  const form = new FormData()
  form.append('file', blob, filename)
  const response = await api.fetch('/voice/stt', { method: 'POST', body: form })
  if (!response.ok) {
    const errText = await response.text().catch(() => '')
    throw new Error(`STT failed (${response.status}): ${errText.slice(0, 120)}`)
  }
  const data = (await response.json()) as { text?: string }
  return (data.text ?? '').trim()
}
